// Single source of truth for in-app help: every feature worth discovering, written once.
// The Help panel reads this list; later, tips and "What's new" can read it too.
// Adding a feature = adding one entry here.

import type { FeatureUsage } from '@/lib/api';

export type HelpCategory = 'Everyday' | 'Prescriptions' | 'Front desk' | 'Billing' | 'Clinic';

/** Who the feature is for — mirrors how routes are gated in App.tsx */
export type HelpAudience =
  | 'all'
  | 'clinical'   // users with a doctor profile (doctor_id)
  | 'frontdesk'  // staff and admin (front-desk routes)
  | 'admin';

export type HelpFeature = {
  id: string;
  category: HelpCategory;
  title: string;
  /** One short line: what the user gains (tip card, tooltip, search) */
  benefit: string;
  /** Where to find it: a short path ("Presets › Medicines") or one plain sentence */
  how: string;
  /** Route prefixes where this feature is relevant ("On this page"). '*' = everywhere */
  pages: string[];
  /** Where "Try it" takes the user */
  to?: string;
  /** Open `to` in a new window (e.g. the TV token screen) */
  newWindow?: boolean;
  /** Keys, with 'mod' = Ctrl (⌘ on Mac) */
  shortcut?: string[];
  audience: HelpAudience;
  /** Plan feature key (see usePermission PLAN_FEATURES) */
  planFeature?: string;
  /** Permission the feature's own UI/API checks, e.g. 'billing:refund' — keep in sync so help never
   *  advertises something the user can't do. (Help is guidance only; routes and API enforce access.) */
  permission?: string;
};

export const HELP_CATEGORIES: HelpCategory[] = ['Everyday', 'Prescriptions', 'Front desk', 'Billing', 'Clinic'];

export const HELP_FEATURES: HelpFeature[] = [
  // ── Everyday ────────────────────────────────────────────────────────────────
  {
    id: 'quick-search', category: 'Everyday', audience: 'all', pages: ['*'],
    title: 'Quick search',
    benefit: 'Find a patient or appointment from anywhere.',
    how: 'Type a name, phone number or MRN.',
    shortcut: ['mod', 'K'],
  },
  {
    id: 'new-patient', category: 'Everyday', audience: 'all', pages: ['*'], permission: 'patients:write',
    title: 'New patient',
    benefit: 'Register a patient without leaving your screen.',
    how: 'Or New Patient in the top bar. Name and phone are enough.',
    shortcut: ['mod', 'N'],
  },
  {
    id: 'share', category: 'Everyday', audience: 'all', pages: ['/prescriptions', '/patients', '/billing', '/frontdesk'], permission: 'billing:read',
    title: 'Share on WhatsApp',
    benefit: 'Send a prescription or bill to the patient.',
    how: 'Print preview › Share, or the WhatsApp icon on an invoice.',
  },

  // ── Prescriptions ───────────────────────────────────────────────────────────
  {
    id: 'templates', category: 'Prescriptions', audience: 'clinical', pages: ['/prescriptions', '/patients', '/presets'], permission: 'prescriptions:read',
    title: 'Templates',
    benefit: 'Fill a common prescription in one click.',
    how: 'Save one from the prescription form. Manage under Presets › Templates.',
    to: '/presets',
  },
  {
    id: 'repeat-rx', category: 'Prescriptions', audience: 'clinical', pages: ['/patients'], permission: 'prescriptions:write',
    title: 'Repeat a prescription',
    benefit: 'Start from the last visit instead of retyping.',
    how: 'Patient › Visits › Copy Rx.',
    to: '/patients',
  },
  {
    id: 'my-medicines', category: 'Prescriptions', audience: 'clinical', pages: ['/prescriptions', '/patients', '/presets'], permission: 'prescriptions:read',
    title: 'Favourite medicines',
    benefit: 'Pre-fill your usual dose, timing and duration.',
    how: 'Presets › Medicines.',
    to: '/presets?tab=medicines',
  },
  {
    id: 'own-entries', category: 'Prescriptions', audience: 'clinical', pages: ['/prescriptions', '/patients', '/presets'], permission: 'prescriptions:read',
    title: 'Your own complaints, diagnoses and tests',
    benefit: 'Fix a typo, or mark "bukhar" the same as Fever so it gets the standard name and code.',
    how: 'Presets › Complaints, Diagnoses or Tests.',
    to: '/presets?tab=complaints',
  },
  {
    id: 'rx-layout', category: 'Prescriptions', audience: 'clinical', pages: ['/prescriptions', '/settings'], permission: 'settings:read',
    title: 'Prescription layout',
    benefit: 'Hide sections you don\'t use and add up to 3 of your own.',
    how: 'Settings › Prescription Layout.',
    to: '/settings?section=prescription-layout',
  },
  {
    id: 'next-visit', category: 'Prescriptions', audience: 'clinical', pages: ['/prescriptions', '/patients'], permission: 'prescriptions:write',
    title: 'Follow-up date',
    benefit: 'Set when the patient should come back.',
    how: 'Next Visit, at the end of the prescription.',
  },
  {
    id: 'documents', category: 'Prescriptions', audience: 'clinical', pages: ['/patients', '/prescriptions'], permission: 'patients:read',
    title: 'Documents and photos',
    benefit: 'Keep reports, photos and IDs with the patient.',
    how: 'Patient › Documents, or Attachments in the prescription.',
    to: '/patients',
  },
  {
    id: 'peds-calculator', category: 'Prescriptions', audience: 'clinical', pages: ['/prescriptions', '/peds-calculator'], planFeature: 'peds_calculator',
    title: 'Pediatric doses',
    benefit: 'Work out a dose from the child\'s weight.',
    how: 'Sidebar › Peds Calculator.',
    to: '/peds-calculator',
  },

  // ── Front desk ──────────────────────────────────────────────────────────────
  {
    id: 'queue', category: 'Front desk', audience: 'frontdesk', pages: ['/frontdesk'], permission: 'queue:read',
    title: 'Queue and tokens',
    benefit: 'Give every walk-in and booked patient a token.',
    how: 'Front Desk › search the patient › add to a doctor.',
    to: '/frontdesk',
  },
  {
    id: 'token-display', category: 'Front desk', audience: 'frontdesk', pages: ['/frontdesk'], permission: 'queue:read',
    title: 'Token screen for the TV',
    benefit: 'Show the token being called in the waiting room.',
    how: 'Open it, move the window to the TV, press F11.',
    to: '/token-display', newWindow: true,
  },

  // ── Billing ─────────────────────────────────────────────────────────────────
  {
    id: 'deposits', category: 'Billing', audience: 'clinical', pages: ['/patients', '/billing'], permission: 'billing:write',
    title: 'Deposits and dues',
    benefit: 'Take an advance or settle an outstanding balance.',
    how: 'Patient › Billing › Record Deposit or Settle Balance.',
    to: '/patients',
  },
  {
    id: 'refunds', category: 'Billing', audience: 'all', pages: ['/billing', '/frontdesk/billing'], permission: 'billing:refund',
    title: 'Refunds',
    benefit: 'Refund all or part of a payment, with a record.',
    how: 'Billing › open the invoice › Refund.',
    to: '/billing',
  },
  {
    id: 'service-prices', category: 'Billing', audience: 'admin', pages: ['/billing', '/settings'],
    title: 'Service prices',
    benefit: 'Set your fees once and pick them when billing.',
    how: 'Settings › Billing › Services Catalog.',
    to: '/settings?section=billing',
  },

  // ── Clinic ──────────────────────────────────────────────────────────────────
  {
    id: 'reports', category: 'Clinic', audience: 'all', pages: ['/reports', '/'], planFeature: 'reports', permission: 'reports:read',
    title: 'Reports',
    benefit: 'Collections and visits for any date range.',
    how: 'Sidebar › Reports.',
    to: '/reports',
  },
  {
    id: 'ipd', category: 'Clinic', audience: 'all', pages: ['/ipd'], planFeature: 'ipd', permission: 'ipd:read',
    title: 'Inpatient (IPD)',
    benefit: 'Beds, daily charges, orders and discharge summaries.',
    how: 'Sidebar › IPD › admit a patient.',
    to: '/ipd',
  },
];

/**
 * Features the Home "Get more out of OneCheckUp" card suggests, most valuable first.
 * `usedWhen` names a /settings/feature-usage flag; once true the tip is never shown.
 * Tips without a signal show until the user clicks "Try it" or "Next tip".
 * Left out on purpose: setup items the Getting Started checklist covers, and refunds.
 */
export const SUGGESTED_TIPS: { id: string; usedWhen?: keyof FeatureUsage }[] = [
  { id: 'templates', usedWhen: 'templates' },
  { id: 'my-medicines', usedWhen: 'favourite_medicines' },
  { id: 'quick-search' },
  { id: 'repeat-rx' },
  { id: 'queue', usedWhen: 'queue' },
  { id: 'token-display' },
  { id: 'next-visit', usedWhen: 'next_visit' },
  { id: 'documents', usedWhen: 'documents' },
  { id: 'peds-calculator' },
  { id: 'deposits', usedWhen: 'deposits' },
  { id: 'share' },
  { id: 'reports' },
  { id: 'ipd', usedWhen: 'ipd' },
];

/** Shortcuts that aren't tied to one feature entry */
export const EXTRA_SHORTCUTS: { keys: string[]; label: string }[] = [
  { keys: ['?'], label: 'Open help' },
  { keys: ['Esc'], label: 'Close a dialog or search box' },
];
